import { NavLink, Routes, Route, Navigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  LayoutDashboard,
  Building2,
  Users,
  CreditCard,
  Flag,
  ClipboardList,
  Settings2,
  ScrollText,
  BarChart2,
  LogOut,
} from 'lucide-react';
import { auth } from './firebase';
import { RequirePlatformAdmin } from './guards/RequirePlatformAdmin';
import { Login } from './pages/Login';
import { OpsDashboard } from './pages/OpsDashboard';
import { Entities } from './pages/Entities';
import { EntityDetail } from './pages/EntityDetail';
import { Users as UsersPage } from './pages/Users';
import { Subscriptions } from './pages/Subscriptions';
import { FeatureFlags } from './pages/FeatureFlags';
import { PlatformSettings } from './pages/PlatformSettings';
import { AuditLog } from './pages/AuditLog';
import { Telemetry } from './pages/Telemetry';
import { Waitlist } from './pages/Waitlist';

const NAV_ITEMS = [
  { to: '/',                 label: 'Dashboard',        icon: LayoutDashboard, end: true },
  { to: '/entities',         label: 'Entities',         icon: Building2 },
  { to: '/users',            label: 'Users',            icon: Users },
  { to: '/subscriptions',    label: 'Subscriptions',    icon: CreditCard },
  { to: '/feature-flags',    label: 'Feature Flags',    icon: Flag },
  { to: '/waitlist',         label: 'Waitlist',         icon: ClipboardList },
  { to: '/platform-settings',label: 'Platform',         icon: Settings2 },
  { to: '/telemetry',        label: 'Analytics',        icon: BarChart2 },
  { to: '/audit-log',        label: 'Audit Log',        icon: ScrollText },
];

function AdminLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-screen bg-bg-deep overflow-hidden">
      {/* Sidebar */}
      <aside className="w-52 flex-shrink-0 flex flex-col bg-bg-surface border-r border-white/[0.06]">
        {/* Brand */}
        <div className="h-14 flex items-center px-4 border-b border-white/[0.06] flex-shrink-0">
          <span className="text-sm font-semibold text-white tracking-tight">OctoPM</span>
          <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary/20 text-primary">
            Admin
          </span>
          <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white/[0.06] text-gray-400">
            Beta
          </span>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary'
                    : 'text-gray-400 hover:text-white hover:bg-white/[0.06]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : ''}`} />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/[0.06] px-2 py-3 flex-shrink-0">
          <button
            onClick={() => signOut(auth)}
            className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <div className="px-6 py-5">
          {children}
        </div>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/*"
        element={
          <RequirePlatformAdmin>
            <AdminLayout>
              <Routes>
                <Route path="/"                  element={<OpsDashboard />} />
                <Route path="/entities"          element={<Entities />} />
                <Route path="/entities/:entityId" element={<EntityDetail />} />
                <Route path="/users"             element={<UsersPage />} />
                <Route path="/subscriptions"     element={<Subscriptions />} />
                <Route path="/feature-flags"     element={<FeatureFlags />} />
                <Route path="/waitlist"          element={<Waitlist />} />
                <Route path="/platform-settings" element={<PlatformSettings />} />
                <Route path="/telemetry"         element={<Telemetry />} />
                <Route path="/audit-log"         element={<AuditLog />} />
                <Route path="*"                  element={<Navigate to="/" replace />} />
              </Routes>
            </AdminLayout>
          </RequirePlatformAdmin>
        }
      />
    </Routes>
  );
}
